import React, { useContext, useEffect, useState } from "react";
import FileUploadState from "../components/Gallery/model/FileUploadState.model";
import FileUploadHandler from "../components/Gallery/model/FileUploadHandler.model";
import FileUpload from "../components/Gallery/model/FileUpload.model";
import LoadedFile from "../components/Gallery/model/LoadedFile.model";
import { HttpErrorType } from "../models/HttpErrorType";
import GalleryFlag from "../components/Gallery/model/GalleryFlag.model";
import EnqueuedFile from "../components/Gallery/model/EnqueuedFile";
import ErrorSnackbarContext from "./ErrorSnackbarContext";

export interface UploadListener {
  serviceId: string;
  onUpload: (context: { loadedFile: LoadedFile; result: unknown | null }, flag?: GalleryFlag) => void;
}

interface EnqueueParams {
  serviceId: string;
  files: EnqueuedFile[];
  afterUpload?: (loadedFile: LoadedFile) => Promise<unknown>;
}

export interface UploadContextState {
  enqueue: (params: EnqueueParams) => void;
  register: (uploadListener: UploadListener) => void;
  unregister: (uploadListener: UploadListener) => void;
  queue: FileUploadState[];
  removeFinished: (serviceId: string, fileId: string) => void;
}

export const UploadContext = React.createContext<UploadContextState>({
  enqueue: () => {},
  register: () => {},
  unregister: () => {},
  removeFinished: () => {},
  queue: [],
});

class UploadContextListeners {
  private listeners: UploadListener[] = [];

  public listenersById(serviceId: string): UploadListener[] {
    return this.listeners.filter((listener) => listener.serviceId === serviceId);
  }

  public register(uploadListener: UploadListener): void {
    const index = this.listeners.indexOf(uploadListener);
    if (index < 0) {
      this.listeners.push(uploadListener);
    }
  }

  public unregister(uploadListener: UploadListener): void {
    const index = this.listeners.indexOf(uploadListener);
    if (index >= 0) {
      this.listeners.splice(index, 1);
    }
  }
}

const uploadListeners = new UploadContextListeners();

export const UploadContextProvider: React.FunctionComponent<{
  fileUploadHandler: FileUploadHandler;
  onHttpError: (error?: HttpErrorType) => void;
  children?: JSX.Element;
}> = (props) => {
  const { fileUploadHandler, onHttpError, children } = props;

  const [queue, setQueue] = useState<FileUploadState[]>([]);
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const { showSnackbar } = useContext(ErrorSnackbarContext);

  const enqueue = (params: EnqueueParams): void => {
    const fileUploads = fileUploadHandler.upload(params.serviceId, params.files, params.afterUpload);
    setUploads((prevState) => [...prevState, ...fileUploads]);
  };

  const removeFinished = (serviceId: string, fileId: string): void => {
    setQueue((prevState) => {
      const index = prevState.findIndex(
        (value) =>
          value.id === fileId && value.serviceId === serviceId && (value.state === "DONE" || value.state === "ERROR"),
      );
      if (index >= 0) {
        const copy = [...prevState];
        copy.splice(index, 1);
        return copy;
      }
      return prevState;
    });
  };

  useEffect(() => {
    setQueue((prevState) => {
      const copy = [...prevState];
      uploads.forEach((upload) => {
        const index = prevState.map((s) => s.id).indexOf(upload.id);
        if (index < 0) {
          copy.push({
            id: upload.id,
            state: upload.state,
            progress: 0,
            serviceId: upload.serviceId,
            name: upload.name,
            flag: upload.flag,
          });
        }
      });
      if (copy.length === prevState.length) {
        return prevState;
      }
      return copy;
    });
    const waiting = uploads.length > 0 && uploads.filter((upload) => upload.state === "RUNNING").length === 0;
    if (waiting) {
      const fileUpload = uploads[0];
      fileUpload.state = "RUNNING";
      setQueue((prevState) => {
        const index = prevState.findIndex((value) => value.id === fileUpload.id);
        if (index >= 0) {
          const copy = [...prevState];
          copy.splice(index, 1, { ...copy[index], state: "RUNNING" });
          return copy;
        }
        return prevState;
      });
      fileUpload
        .upload()
        .then((loadedFile) => {
          if (fileUpload.afterUpload) {
            return fileUpload.afterUpload(loadedFile).then((result) => ({ loadedFile, result }));
          }
          return { loadedFile, result: null };
        })
        .then((response) => {
          uploadListeners
            .listenersById(fileUpload.serviceId)
            .forEach((listener) => listener.onUpload(response, fileUpload.flag));
          setQueue((prevState) => {
            const index = prevState.findIndex((value) => value.id === fileUpload.id);
            if (index >= 0) {
              const copy = [...prevState];
              copy.splice(index, 1, {
                ...copy[index],
                state: "DONE",
                id: response.loadedFile.id,
              });
              return copy;
            }
            return prevState;
          });
          removeFinished(fileUpload.serviceId, response.loadedFile.id);
        })
        .then(() => {
          const finishedMessage = fileUpload.notifications?.finished;
          if (finishedMessage) {
            showSnackbar(finishedMessage);
          }
        })
        .catch((error) => {
          const errorMessage = fileUpload.notifications?.error;
          if (errorMessage) {
            showSnackbar(errorMessage, "error");
          } else {
            onHttpError(error);
          }
          setQueue((prevState) => {
            const index = prevState.findIndex((value) => value.id === fileUpload.id);
            if (index >= 0) {
              const copy = [...prevState];
              copy.splice(index, 1, { ...copy[index], state: "ERROR" });
              return copy;
            }
            return prevState;
          });
        })
        .finally(() => {
          setUploads((prevState) => {
            const index = prevState.findIndex((value) => value.id === fileUpload.id);
            if (index >= 0) {
              const copy = [...prevState];
              copy.splice(index, 1);
              return copy;
            }
            return prevState;
          });
        });
      fileUpload.progress.subscribe({
        next(progress: number) {
          setQueue((prevState) => {
            const index = prevState.findIndex((value) => value.id === fileUpload.id);
            if (index >= 0) {
              const copy = [...prevState];
              copy.splice(index, 1, { ...copy[index], progress });
              return copy;
            }
            return prevState;
          });
        },
      });
    }
  }, [uploads]);

  const register = (uploadListener: UploadListener): void => {
    uploadListeners.register(uploadListener);
  };

  const unregister = (uploadListener: UploadListener): void => {
    uploadListeners.unregister(uploadListener);
  };

  return (
    <UploadContext.Provider
      value={{
        enqueue,
        queue,
        register,
        unregister,
        removeFinished,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export default UploadContext;
